/**
 * Creates an HTMLImageElement from a URL
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function getRotatedSize(width, height, rotation) {
  const rotRad = (rotation * Math.PI) / 180;

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {string} imageSrc - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 * @param {boolean} flip - optional flip parameter
 * @param {number} minWidth - minimum width check
 * @param {string} outputFormat - 'image/jpeg' or 'image/webp'
 * @param {number} quality - 0 to 1
 */
export default async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
  minWidth = 750,
  outputFormat = 'image/jpeg',
  quality = 0.8
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const rotRad = (rotation * Math.PI) / 180;

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = getRotatedSize(
    image.width,
    image.height,
    rotation
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Fill bounding box with pure white background so transparent PNGs don't turn black
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, bBoxWidth, bBoxHeight);

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw image
  ctx.drawImage(image, 0, 0);

  // croppedAreaPixels values are bounding box relative
  // extract the cropped image using these values
  const data = ctx.getImageData(
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height
  );

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Ensure solid white background on destination canvas
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // paste generated rotate image at the top left corner
  ctx.putImageData(data, 0, 0);

  // If cropped width is smaller than minWidth, upscale smoothly so the image always satisfies the resolution
  let finalCanvas = canvas;
  if (canvas.width < minWidth && canvas.width > 0) {
    const scaleFactor = minWidth / canvas.width;
    const scaledCanvas = document.createElement('canvas');
    scaledCanvas.width = minWidth;
    scaledCanvas.height = Math.round(canvas.height * scaleFactor);
    const scaledCtx = scaledCanvas.getContext('2d');
    if (scaledCtx) {
      scaledCtx.fillStyle = '#FFFFFF';
      scaledCtx.fillRect(0, 0, scaledCanvas.width, scaledCanvas.height);
      scaledCtx.imageSmoothingEnabled = true;
      scaledCtx.imageSmoothingQuality = 'high';
      scaledCtx.drawImage(canvas, 0, 0, scaledCanvas.width, scaledCanvas.height);
      finalCanvas = scaledCanvas;
    }
  }

  // As Blob
  return new Promise((resolve, reject) => {
    finalCanvas.toBlob((file) => {
      if (file) {
        resolve(file); // Result is a Blob
      } else {
        reject(new Error('Canvas is empty'));
      }
    }, outputFormat, quality);
  });
}
